/* src/components/journal/JournalPage.js */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProfile } from '../../contexts/ProfileContext';
import JournalEntry from './JournalEntry';
import AddJournalEntryModal from './AddJournalEntryModal';
import EditJournalEntryModal from './EditJournalEntryModal';
import ConfirmationModal from '../ui/ConfirmationModal';
import PaginationControls from '../ui/PaginationControls';

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex justify-center items-center py-8">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
  </div>
);

const JournalPage = () => {
  const { entryId } = useParams();
  const {
    activeProfile,
    updateJournalEntry,
    removeJournalEntry,
    isLoading: isLoadingProfile,
    error: profileError,
  } = useProfile();
  const navigate = useNavigate();

  // --- State for List View ---
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState(() => new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const itemsPerPageOptions = [5, 10, 25, 50];

  // --- State for Single Entry View & Modals ---
  const [editingEntry, setEditingEntry] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [entryToDeleteId, setEntryToDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');

  // --- Memoized Data ---
  const allJournalEntries = useMemo(() => {
    const journal = Array.isArray(activeProfile?.journal) ? activeProfile.journal : [];
    return [...journal].sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
  }, [activeProfile?.journal]);

  // --- Find Entry for Single View ---
  const entryToView = useMemo(() => {
    if (!entryId || !allJournalEntries) return null;
    return allJournalEntries.find(e => e.id === entryId);
  }, [entryId, allJournalEntries]);

  // --- Data for List View (Filtering & Pagination) ---
  const filteredJournalEntries = useMemo(() => {
    if (!allJournalEntries) return [];
    if (selectedTags.size === 0) return allJournalEntries;
    return allJournalEntries.filter(entry =>
      Array.isArray(entry.tags) && entry.tags.some(tag => selectedTags.has(tag))
    );
  }, [allJournalEntries, selectedTags]);

  const paginationData = useMemo(() => {
    const totalItems = filteredJournalEntries.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
    const validatedCurrentPage = Math.max(1, Math.min(currentPage, totalPages));
    const startIndex = (validatedCurrentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedEntries = filteredJournalEntries.slice(startIndex, endIndex);
    return { totalItems, totalPages, paginatedEntries, currentPage: validatedCurrentPage };
  }, [filteredJournalEntries, currentPage, itemsPerPage]);

  const { totalItems, totalPages, paginatedEntries, currentPage: validatedCurrentPage } = paginationData;

  const availableTags = useMemo(() => {
    const tags = Array.isArray(activeProfile?.journalTags) ? activeProfile.journalTags : [];
    return [...tags].sort();
  }, [activeProfile?.journalTags]);

  // --- Callbacks & Handlers ---
  const handleTagClick = useCallback((tag) => {
    setCurrentPage(1);
    setSelectedTags(prev => {
      const n = new Set(prev);
      n.has(tag) ? n.delete(tag) : n.add(tag);
      return n;
    });
  }, []);

  const clearTagFilters = useCallback(() => {
    setSelectedTags(new Set());
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      const el = document.getElementById('journal-list-container');
      if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  }, [totalPages]);

  const handleItemsPerPageChange = useCallback((num) => {
    const n = Number(num);
    if (!isNaN(n) && n > 0) {
      setItemsPerPage(n);
      setCurrentPage(1);
    }
  }, []);

  const handleOpenAddModal = useCallback(() => setIsAddModalOpen(true), []);
  const handleCloseAddModal = useCallback(() => setIsAddModalOpen(false), []);

  // Single Entry View / Modal Handlers
  const handleStartEdit = useCallback((id) => {
    const entry = allJournalEntries.find(e => e.id === id);
    if (entry) setEditingEntry(entry);
    else console.error("Attempted to edit non-existent entry:", id);
  }, [allJournalEntries]);

  const handleCancelEdit = useCallback(() => setEditingEntry(null), []);

  const handleSaveEdit = useCallback(async (id, updatedData) => {
    if (!activeProfile?.id || !updateJournalEntry) return false;
    try {
      const success = await updateJournalEntry(activeProfile.id, id, updatedData);
      if (success) setEditingEntry(null);
      else throw new Error("Update failed via context.");
      return success;
    } catch (error) {
      console.error("Edit save error:", error);
      alert("Save failed.");
      return false;
    }
  }, [activeProfile?.id, updateJournalEntry]);

  const handleDeleteClick = useCallback((id) => {
    setEntryToDeleteId(id);
    setDeleteConfirmInput('');
    setIsDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    const req = "delete";
    if (deleteConfirmInput.trim().toLowerCase() !== req) {
      alert(`Type '${req}'`);
      return;
    }
    if (!entryToDeleteId || !activeProfile?.id || !removeJournalEntry || isDeleting) return;
    setIsDeleting(true);
    try {
      const success = await removeJournalEntry(activeProfile.id, entryToDeleteId);
      if (success) {
        navigate('/journal', { replace: true });
      } else {
        throw new Error("Delete failed via context.");
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert("Delete failed.");
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setEntryToDeleteId(null);
      setDeleteConfirmInput('');
    }
  }, [entryToDeleteId, activeProfile?.id, removeJournalEntry, isDeleting, deleteConfirmInput, navigate]);

  const handleCancelDelete = useCallback(() => {
    setIsDeleteModalOpen(false);
    setEntryToDeleteId(null);
    setDeleteConfirmInput('');
  }, []);

  // --- Effect Hooks ---
  useEffect(() => {
    if (!entryId) {
      if (validatedCurrentPage !== currentPage && validatedCurrentPage <= totalPages) {
        setCurrentPage(validatedCurrentPage);
      } else if (currentPage > totalPages && totalPages > 0) {
        setCurrentPage(totalPages);
      }
    }
  }, [validatedCurrentPage, totalPages, currentPage, entryId]);

  useEffect(() => {
    if (entryId) {
      // Reset state when switching back from list view
    } else {
      setEditingEntry(null);
      setIsDeleteModalOpen(false);
      setEntryToDeleteId(null);
    }
  }, [entryId]);

  // --- Render Logic ---
  // Initial Loading State
  if (isLoadingProfile && !activeProfile) {
    return <LoadingSpinner />;
  }

  // No Active Profile State
  if (!activeProfile) {
    return (
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md mx-auto text-center mt-12 border border-gray-200">
        <div className="text-5xl text-indigo-400 mb-4">📔</div>
        <h1 className="text-2xl font-bold text-gray-800 mb-3">No Profile Selected</h1>
        <p className="text-gray-600 mb-6">Please select a profile to view your journal.</p>
        <button 
          onClick={() => navigate('/profile-selection')} 
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg shadow-sm hover:bg-indigo-700 transition-colors"
        >
          Select Profile
        </button>
      </div>
    );
  }

  // Profile Context Error State
  if (profileError) {
    return (
      <div className="p-6 text-center bg-red-50 border border-red-200 rounded-lg shadow max-w-md mx-auto mt-12">
        <div className="text-3xl mb-2">⚠️</div>
        <h2 className="text-lg font-semibold text-red-700 mb-2">Error Loading Profile</h2>
        <p className="text-red-600">{profileError}</p>
      </div>
    );
  }

  // --- Conditional Rendering: Single Entry vs. List View ---
  if (entryId) {
    // === Single Entry View ===
    if (isLoadingProfile) return <LoadingSpinner />;
    
    if (!entryToView) return (
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md mx-auto text-center mt-12 border border-gray-200">
        <div className="text-5xl text-amber-400 mb-4">🔍</div>
        <h1 className="text-2xl font-bold text-gray-800 mb-3">Entry Not Found</h1>
        <p className="text-gray-600 mb-6">The journal entry you're looking for doesn't exist or has been removed.</p>
        <button 
          onClick={() => navigate('/journal')} 
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg shadow-sm hover:bg-indigo-700 transition-colors"
        >
          Back to Journal
        </button>
      </div>
    );

    return (
      <div className="p-4 sm:p-6 bg-gray-100 min-h-screen">
        <div className="max-w-2xl mx-auto">
          {/* Back Button */}
          <div className="mb-4 flex items-center">
            <button 
              onClick={() => navigate('/journal')} 
              className="group flex items-center text-sm text-indigo-600 hover:text-indigo-800 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-md px-2 py-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 group-hover:-translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Journal
            </button>
          </div>
          
          {/* Single Entry Display */}
          <div className="animate-[fadeIn_0.3s_ease-in]">
            <JournalEntry
              entry={entryToView}
              onEdit={handleStartEdit}
              onDelete={handleDeleteClick}
              onNavigate={navigate}
            />
          </div>
          
          {/* Modals for this specific entry */}
          <EditJournalEntryModal
            entry={editingEntry}
            onSave={handleSaveEdit}
            onCancel={handleCancelEdit}
          />
          
          <ConfirmationModal
            isOpen={isDeleteModalOpen}
            title="Delete Journal Entry"
            message="This action cannot be undone. Are you sure you want to delete this entry?"
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
            confirmText={isDeleting ? "Deleting..." : "Delete"}
            isDestructive={true}
            requiredInputText="delete"
            inputLabel="Type 'delete' to confirm"
            inputValue={deleteConfirmInput}
            onInputChange={(e) => setDeleteConfirmInput(e.target.value)}
          />
        </div>
      </div>
    );

  } else {
    // === List View ===
    return (
      <div className="p-4 sm:p-6 bg-gray-100 min-h-screen">
        <div className="max-w-4xl mx-auto">
          {/* Header Section */}
          <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
                  {activeProfile.username ? `${activeProfile.username}'s Journal` : 'Journal'}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  Track your training progress and thoughts
                </p>
              </div>
              
              <button
                onClick={handleOpenAddModal}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 
                          shadow-sm transition-colors text-sm font-medium flex items-center
                          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                aria-haspopup="dialog"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Entry
              </button>
            </div>
          </div>

          {/* Filters Section */}
          {availableTags.length > 0 && (
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                  <h2 className="text-sm font-medium text-gray-700 mb-2">Filter by Tags</h2>
                  <div className="flex flex-wrap gap-2">
                    {availableTags.map((tag) => {
                      const isSelected = selectedTags.has(tag);
                      return (
                        <button
                          key={tag}
                          onClick={() => handleTagClick(tag)}
                          className={`text-xs font-medium px-2 py-1 rounded-full transition-colors duration-150 cursor-pointer border focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                            isSelected
                              ? 'bg-indigo-600 text-white border-indigo-700 ring-indigo-500 hover:bg-indigo-700 shadow-sm'
                              : 'bg-white text-indigo-700 border-indigo-300 ring-indigo-300 hover:bg-indigo-50 hover:border-indigo-500'
                          }`}
                          aria-pressed={isSelected}
                        >
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {selectedTags.size > 0 && (
                  <button
                    onClick={clearTagFilters}
                    className="text-xs text-gray-500 hover:text-red-600 hover:underline px-2 py-1"
                    title="Clear filters"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Journal List Container */}
          <div id="journal-list-container" className="mt-2 space-y-3 max-w-2xl mx-auto animate-[fadeIn_0.3s_ease-in]">
            {/* Loading/Empty States */}
            {isLoadingProfile && !allJournalEntries.length ? (
              <LoadingSpinner />
            ) : !allJournalEntries.length ? (
              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
                <div className="text-5xl mb-4">📝</div>
                <h2 className="text-xl font-semibold text-gray-700 mb-3">Your Journal is Empty</h2>
                <p className="text-gray-600 mb-6">Start by adding entries or complete training sessions!</p>
                <button
                  onClick={handleOpenAddModal}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg shadow-sm hover:bg-indigo-700 transition-colors"
                >
                  Create First Entry
                </button>
              </div>
            ) : filteredJournalEntries.length === 0 && selectedTags.size > 0 ? (
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-center">
                <div className="text-4xl mb-3">🔍</div>
                <h2 className="text-lg font-semibold text-gray-700 mb-2">No Matching Entries</h2>
                <p className="text-gray-600 mb-4">No entries match the selected tags.</p>
                <button
                  onClick={clearTagFilters}
                  className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              // Render Paginated Entries
              paginatedEntries.map((entry) => (
                <JournalEntry
                  key={entry.id}
                  entry={entry}
                  onEdit={handleStartEdit}
                  onDelete={handleDeleteClick}
                  onNavigate={navigate}
                />
              ))
            )}
          </div>

          {/* Pagination Controls - Only show if needed */}
          {(totalPages > 1 || totalItems > itemsPerPageOptions[0]) && filteredJournalEntries.length > 0 && (
            <div className="mt-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200 max-w-2xl mx-auto">
              <PaginationControls
                currentPage={validatedCurrentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                totalItems={totalItems}
                itemsPerPageOptions={itemsPerPageOptions}
              />
            </div>
          )}

          {/* Add Modal */}
          <AddJournalEntryModal
            isOpen={isAddModalOpen}
            onClose={handleCloseAddModal}
            onAddComplete={() => {
              console.log("Add complete signal received.");
              // Optional: You could add logic here to display a success toast or notification
            }}
          />

          {/* Edit Modal */}
          <EditJournalEntryModal
            entry={editingEntry}
            onSave={handleSaveEdit}
            onCancel={handleCancelEdit}
          />

          {/* Delete Confirmation Modal */}
          <ConfirmationModal
            isOpen={isDeleteModalOpen}
            title="Delete Journal Entry"
            message="This action cannot be undone. Are you sure you want to delete this entry?"
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
            confirmText={isDeleting ? "Deleting..." : "Delete"}
            isDestructive={true}
            requiredInputText="delete"
            inputLabel="Type 'delete' to confirm"
            inputValue={deleteConfirmInput}
            onInputChange={(e) => setDeleteConfirmInput(e.target.value)}
          />
        </div>
      </div>
    );
  }
};

export default JournalPage;